import React from "react";
import SEO from "../components/SEO";
import teamsScopeImg from "../images/teams-scope.svg";
import { DEMO_LINK } from "../components/demoBooking";

export default function Teams() {
  return (
    <main className="page container">
      <SEO
        title="BossDrop | Teams"
        description="BossDrop for Teams: automate status updates, escalations, tasks, and weekly exec briefs for 5 to 25 seat teams inside larger orgs."
        canonical="https://bossdrop.ai/teams"
        url="https://bossdrop.ai/teams"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Teams",
              item: "https://bossdrop.ai/teams",
            },
          ],
        }}
      />

      <section className="hero">
        <h1 className="h1 reveal" data-reveal>
          Teams
        </h1>

        <p className="sub reveal" data-reveal style={{ "--d": "80ms" }}>
          For 5 to 25 seat teams inside larger orgs. BossDrop runs the weekly
          cadence: status collection, risk detection, escalations, and a clean
          exec-ready brief.
        </p>

        <div className="kpis reveal" data-reveal style={{ "--d": "140ms" }}>
          <div className="pill">
            Priced <strong>per team, not per seat</strong>
          </div>
          <div className="pill">Works in Teams + Planner</div>
          <div className="pill">No new process to maintain</div>
        </div>

        <div className="ctaBand reveal" data-reveal style={{ "--d": "200ms" }}>
          <a className="btn btnPrimary spacing" {...DEMO_LINK}>
            Book a demo
          </a>
          <a className="btn btnPrimary" href="/contact-sales">
            Talk to sales
          </a>
        </div>
      </section>

      <figure style={{ margin: "8px auto 22px", maxWidth: 900 }}>
        <div className="figWrap">
          <img
            src={teamsScopeImg}
            alt="Teams scope: one team pod. A single team lead sets intent, one BossDrop AI layer runs the cadence for about six team members across chat, tasks, and calendar. One team, one roster, one cadence."
            loading="lazy"
            decoding="async"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </div>
      </figure>

      <section className="grid pricing reveal" data-reveal>
        <div className="card price featured">
          <div className="priceHeader">
            <h3>Team</h3>
            <div className="priceTag">Talk to us</div>
            <p className="muted">
              One team, one operating cadence. Priced per team, not per
              seat. We quote a specific number and scope in your pilot
              conversation.
            </p>
          </div>

          <ul className="bullets">
            <li>Dedicated workspace/tenant for your team</li>
            <li>Status collection via Teams/Slack DMs</li>
            <li>Weekly exec-ready brief (owners, ETAs, risks, decisions)</li>
            <li>Risk detection + automatic escalation with a concrete ask</li>
            <li>
              Auto-created &amp; updated tasks in Jira, Asana, Linear, ClickUp,
              Notion, monday.com or Planner
            </li>
            <li>
              Auto-scheduled meetings only when needed (with agenda + pre-read)
            </li>
            <li>Approval queue &amp; exportable audit log</li>
          </ul>

          <a className="btn btnPrimary" {...DEMO_LINK}>
            Book a demo
          </a>
        </div>
      </section>

      <section className="panel reveal" data-reveal>
        <h2 className="h2">What it replaces</h2>
        <div className="grid cards">
          <div className="card">
            <h3>Status meetings</h3>
            <p className="muted">
              BossDrop collects updates asynchronously and rolls them into a
              clean leadership view.
            </p>
          </div>
          <div className="card">
            <h3>Manual escalation chasing</h3>
            <p className="muted">
              It detects drift and escalates early with ownership, context, and
              a specific next action.
            </p>
          </div>
          <div className="card">
            <h3>Hand-built exec reports</h3>
            <p className="muted">
              No more Friday-night rollups. The brief is generated from your
              real work streams.
            </p>
          </div>
        </div>
      </section>

      <section className="panel panelDark reveal" data-reveal>
        <h2 className="h2">Need org-wide rollups, SSO, and controls?</h2>
        <p className="muted" style={{ marginTop: 6 }}>
          Teams is built for small groups inside larger companies. When you want
          cross-team rollups, enterprise controls, and a formal rollout path, go
          Enterprise.
        </p>
        <div className="ctaBand" style={{ margin: 0 }}>
          <a className="btn btnPrimary spacing" href="/enterprise">
            View Enterprise
          </a>
          <a className="btn btnPrimary" href="/contact-sales">
            Contact sales
          </a>
        </div>
      </section>
    </main>
  );
}
